import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import type { AudioEngine } from './engine';

export type EngineLoader = () => Promise<AudioEngine>;

interface AudioContextValue {
  /** Resolves to the engine. The first call must happen inside a user gesture. */
  getEngine: EngineLoader;
  /** Milliseconds to subtract from tap times: the learner's calibrated device delay. */
  latencyOffsetMs: number;
}

let shared: Promise<AudioEngine> | null = null;
let loaded: AudioEngine | null = null;

/** Loads the Tone.js adapter in its own chunk and starts the audio context, once. */
const loadToneEngine: EngineLoader = () => {
  shared ??= import('./toneEngine')
    .then(({ createToneEngine }) => createToneEngine())
    .then((engine) => {
      loaded = engine;
      return engine;
    })
    .catch((error: unknown) => {
      shared = null; // let the next gesture try again
      throw error;
    });
  return shared;
};

const AudioSettingsContext = createContext<AudioContextValue>({
  getEngine: loadToneEngine,
  latencyOffsetMs: 0,
});

interface AudioProviderProps {
  /** 0..1 */
  volume: number;
  latencyOffsetMs: number;
  /** Replaces the engine; tests pass a fake. */
  loader?: EngineLoader;
  children: ReactNode;
}

/**
 * Gives the subtree access to sound. Volume and latency come from the app layer,
 * which keeps this module free of any dependency on the settings store.
 */
export function AudioProvider({ volume, latencyOffsetMs, loader, children }: AudioProviderProps) {
  const getEngine = useMemo<EngineLoader>(() => {
    const base = loader ?? loadToneEngine;
    return async () => {
      const engine = await base();
      engine.setVolume(volume);
      return engine;
    };
  }, [loader, volume]);

  useEffect(() => {
    loaded?.setVolume(volume);
  }, [volume]);

  // Leaving the app's audio subtree (e.g. signing out) silences whatever is playing.
  useEffect(() => () => loaded?.stopAll(), []);

  const value = useMemo(() => ({ getEngine, latencyOffsetMs }), [getEngine, latencyOffsetMs]);
  return <AudioSettingsContext value={value}>{children}</AudioSettingsContext>;
}

export function useAudio(): AudioContextValue {
  return useContext(AudioSettingsContext);
}
